/**
 * Image-URL → exact card lookup.
 *
 * Both the Pokémon TCG API and Scryfall embed the card's identifier inside
 * their image CDN URLs. We parse the URL once, hit the exact-card endpoint
 * (no fuzzy text matching), and return the current market unit price.
 *
 * This is way more accurate than name/set/number search because the image URL
 * was set when the user originally picked the card from quickCardLookup —
 * any ambiguity was resolved at that moment, so a refresh is just "what does
 * THIS specific card cost today?".
 *
 * Callers fall back to fmvLookupForCard() if this returns null (no image, can't
 * parse the URL, or the API request fails).
 */
import { Settings } from "./prefs";

export interface ImageFmvResult {
  unit: number;
  variant: string;  // e.g. "holofoil (TCGplayer Market)" or "USD (non-foil)"
  source: string;   // "Pokemon TCG API" or "Scryfall"
}

/* ---------- URL parsers ---------- */

/** "https://images.pokemontcg.io/sv1/176.png" or "_hires.png" → "sv1-176" */
export function pokemonCardIdFromUrl(url: string): string | null {
  // Path pattern: /{setId}/{number}[_hires].{ext}[?query]
  const m = url.match(/images\.pokemontcg\.io\/([^/]+)\/([^/.?]+?)(?:_hires)?\.(?:png|jpg|jpeg|webp)(?:\?.*)?$/i);
  if (!m) return null;
  return `${m[1]}-${m[2]}`;
}

/** Scryfall image URLs: /scryfall-cards/.../{uuid}.{ext} */
export function scryfallCardIdFromUrl(url: string): string | null {
  const m = url.match(/scryfall\.io\/.+\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:_[a-z]+)?\.(?:png|jpg|jpeg|webp)(?:\?.*)?$/i);
  return m ? m[1] : null;
}

/* ---------- Direct fetchers ---------- */

const POKEMON_VARIANT_PRIORITY = [
  "holofoil", "reverseHolofoil", "normal",
  "1stEditionHolofoil", "unlimitedHolofoil", "1stEdition", "1stEditionNormal",
];

async function fetchPokemonPrice(cardId: string): Promise<ImageFmvResult | null> {
  const url = `https://api.pokemontcg.io/v2/cards/${encodeURIComponent(cardId)}`;
  const headers: Record<string, string> = {};
  const key = Settings.get().pokemonTcgApiKey;
  if (key) headers["X-Api-Key"] = key;
  let r: Response;
  try { r = await fetch(url, { headers }); }
  catch { return null; }
  if (!r.ok) return null;
  let j: any;
  try { j = await r.json(); } catch { return null; }
  const card = j?.data;
  if (!card) return null;
  const prices = card.tcgplayer?.prices || {};
  // Pick the first priority variant with a market price.
  const variants = Object.keys(prices);
  variants.sort((a, b) => {
    const pa = POKEMON_VARIANT_PRIORITY.indexOf(a);
    const pb = POKEMON_VARIANT_PRIORITY.indexOf(b);
    return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb);
  });
  for (const v of variants) {
    const p = prices[v];
    if (p && typeof p.market === "number" && p.market > 0) {
      return {
        unit: +Number(p.market).toFixed(2),
        variant: `${v} (TCGplayer Market)`,
        source: "Pokemon TCG API",
      };
    }
  }
  return null;
}

async function fetchScryfallPrice(cardId: string): Promise<ImageFmvResult | null> {
  const url = `https://api.scryfall.com/cards/${encodeURIComponent(cardId)}`;
  let r: Response;
  try { r = await fetch(url); }
  catch { return null; }
  if (!r.ok) return null;
  let card: any;
  try { card = await r.json(); } catch { return null; }
  const usd = card?.prices?.usd;
  const usdFoil = card?.prices?.usd_foil;
  if (usd) {
    return { unit: +Number(usd).toFixed(2), variant: "USD (non-foil)", source: "Scryfall" };
  }
  if (usdFoil) {
    return { unit: +Number(usdFoil).toFixed(2), variant: "USD foil", source: "Scryfall" };
  }
  return null;
}

/* ---------- Public entry point ---------- */

/**
 * Try to derive an exact-card unit price from the row's Image URL.
 * Returns null when:
 *   - row has no Image URL
 *   - URL doesn't match a known CDN pattern
 *   - API request fails or returns no price
 * In all those cases the caller should fall back to the fuzzy fmvLookup flow.
 */
export async function fmvFromImageUrl(row: Record<string, any>): Promise<ImageFmvResult | null> {
  const imgUrl = String(row["Image URL"] || "").trim();
  if (!imgUrl) return null;

  // Try Pokémon first — overwhelming majority of our thumbnails.
  const pkmnId = pokemonCardIdFromUrl(imgUrl);
  if (pkmnId) return await fetchPokemonPrice(pkmnId);

  // Then Scryfall (MTG).
  const sfId = scryfallCardIdFromUrl(imgUrl);
  if (sfId) return await fetchScryfallPrice(sfId);

  return null;
}
